import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faChevronUp, faChevronDown, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import Countdown from 'react-countdown';
import './App.css';
import NewsFeed from './components/NewsFeed';
import ErrorPage from './components/ErrorPage';

const CountdownApp = () => {
  const getSavedValue = (key, defaultValue) => {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  };

  const [showCustomizationPanel, setShowCustomizationPanel] = useState(false);
  const [countdownTheme, setCountdownTheme] = useState(getSavedValue('countdownTheme', 'default'));
  const [background, setBackground] = useState(getSavedValue('background', 'gta-vi-trailer2-art.jpeg'));
  const [isFooterVisible, setIsFooterVisible] = useState(getSavedValue('isFooterVisible', true));
  const [showControls, setShowControls] = useState(true);
  const [isNewsFeedOpen, setIsNewsFeedOpen] = useState(false);
  const [showFooterToggle, setShowFooterToggle] = useState(getSavedValue('showFooterToggle', true));
  const [showNewsToggle, setShowNewsToggle] = useState(getSavedValue('showNewsToggle', true));
  
  const videoRef = useRef(null);
  const mouseTimeoutRef = useRef(null);

  const releaseDate = new Date('November 19, 2026 00:00:00 UTC');

  useEffect(() => {
    localStorage.setItem('countdownTheme', JSON.stringify(countdownTheme));
  }, [countdownTheme]);

  useEffect(() => {
    localStorage.setItem('background', JSON.stringify(background));
  }, [background]);

  useEffect(() => {
    localStorage.setItem('isFooterVisible', JSON.stringify(isFooterVisible));
  }, [isFooterVisible]);
  
  useEffect(() => {
    localStorage.setItem('showFooterToggle', JSON.stringify(showFooterToggle));
  }, [showFooterToggle]);
  
  useEffect(() => {
    localStorage.setItem('showNewsToggle', JSON.stringify(showNewsToggle));
  }, [showNewsToggle]);

  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);

      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }

      mouseTimeoutRef.current = setTimeout(() => {
        if (!showCustomizationPanel && !isNewsFeedOpen) {
          setShowControls(false);
        }
      }, 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);

    handleMouseMove();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
    };
  }, [showCustomizationPanel, isNewsFeedOpen]);

  const toggleCustomizationPanel = () => {
    setShowCustomizationPanel(!showCustomizationPanel);
    if (!showCustomizationPanel) setIsNewsFeedOpen(false); 
  };

  const toggleNewsFeed = () => {
    setIsNewsFeedOpen(!isNewsFeedOpen);
    if (!isNewsFeedOpen) setShowCustomizationPanel(false); 
  };
  
  const closeNewsFeed = () => {
    setIsNewsFeedOpen(false);
  };

  const toggleFooter = () => {
    setIsFooterVisible(!isFooterVisible);
  };

  const handleBackgroundChange = (e) => {
    setBackground(e.target.value);
  };

  const handleThemeChange = (e) => {
    setCountdownTheme(e.target.value);
  };
  
  const handleFooterToggleVisibilityChange = (e) => {
    setShowFooterToggle(e.target.checked);
  };
  
  const handleNewsToggleVisibilityChange = (e) => {
    setShowNewsToggle(e.target.checked);
  };

  const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return <h1></h1>;
    } else {
      return (
        <div className={`countdown ${countdownTheme}-countdown`}>
          <div className="time">
            <span>{days}</span>
            <p>Days</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{hours}</span>
            <p>Hours</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{minutes}</span>
            <p>Minutes</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{seconds}</span>
            <p>Seconds</p>
            <div className="time-ring"></div>
          </div>
        </div>
      );
    }
  };

  const getBackground = () => {
    if (background.endsWith('.mp4')) {
      return (
        <video ref={videoRef} autoPlay muted loop playsInline>
          <source src={`${process.env.PUBLIC_URL}/img/backgrounds/videos/${background}`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    } else {
      return <img src={`${process.env.PUBLIC_URL}/img/backgrounds/${background}`} alt="GTA VI Background" />;
    }
  };

  return (
    <div className="container">
      <div className="bg-container">
        {getBackground()}
      </div>
      <div className="overlay"></div>

      {showNewsToggle && (
        <button
          onClick={toggleNewsFeed}
          className={`news-feed-toggle ${showControls || isNewsFeedOpen ? 'visible' : ''} ${isNewsFeedOpen ? 'news-open' : ''}`}
          aria-label="Toggle News Feed"
        >
          <FontAwesomeIcon icon={faNewspaper} />
        </button>
      )}

      <NewsFeed isOpen={isNewsFeedOpen} onClose={closeNewsFeed} />

      <div className="content">
        <img height="100px" src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Grand_Theft_Auto_VI_logo_%28with_gradient%29.svg/2560px-Grand_Theft_Auto_VI_logo_%28with_gradient%29.svg.png"></img>

        <Countdown
          date={releaseDate}
          renderer={renderer}
        />

        <p className="release-date">NOVEMBER 2026</p>
      </div>

      <button
        onClick={toggleCustomizationPanel}
        className={`customize-toggle ${showControls || showCustomizationPanel || isNewsFeedOpen ? 'visible' : ''}`}
      >
        <FontAwesomeIcon icon={faGear} /> Customize
      </button>

      <div className={`customization-panel ${showCustomizationPanel ? 'show' : ''}`}>
        <div className="panel-header">
          <h2>Customize</h2>
          <button className="close-btn" onClick={toggleCustomizationPanel}>×</button>
        </div>

        <div className="option">
          <label htmlFor="background">Background:</label>
          <select id="background" value={background} onChange={handleBackgroundChange}>
            <option value="gta-vi-artwork-bg.webp">GTA VI Artwork</option>
            <option value="gta-vi-trailer2-art.jpeg">GTA VI Trailer 2 Art</option>
            <option value="gta-vi-vicecity-sign.jpeg">Vice City Sign</option>
            <option value="vice-city-beach.jpg">Vice City Beach</option>
            <option value="gta-vi-bg-trailer.mp4">GTA VI Trailer Video</option>
            <option value="gta-vi-bg-trailer-2.mp4">GTA VI Trailer 2 Video</option>
          </select>
        </div>

        <div className="option">
          <label htmlFor="countdownTheme">Theme:</label>
          <select id="countdownTheme" value={countdownTheme} onChange={handleThemeChange}>
            <option value="default">Default</option>
            <option value="minimal">Minimal</option>
          </select>
        </div>
        
        <div className="checkbox-option">
          <label>
            <input 
              type="checkbox"
              id="showFooterToggle"
              checked={showFooterToggle}
              onChange={handleFooterToggleVisibilityChange}
              className="checkbox-input"
            />
            <span className="custom-checkbox"></span>
            Show Footer Toggle
          </label>
        </div>
        
        <div className="checkbox-option">
          <label>
            <input 
              type="checkbox"
              id="showNewsToggle"
              checked={showNewsToggle}
              onChange={handleNewsToggleVisibilityChange}
              className="checkbox-input"
            />
            <span className="custom-checkbox"></span>
            Show News Toggle
          </label>
        </div>
      </div>

      {showFooterToggle && (
        <button
          className={`footer-toggle ${isFooterVisible ? '' : 'collapsed'} ${showControls || isNewsFeedOpen ? 'visible' : ''}`}
          onClick={toggleFooter}
        >
          <FontAwesomeIcon icon={isFooterVisible ? faChevronUp : faChevronDown} />
        </button>
      )}

      <footer className={`footer ${isFooterVisible ? '' : 'hidden'}`}>
        <div className="disclaimer">
          This is an unofficial fan-made countdown clock. GTA and all related marks are trademarks of Take-Two Interactive and Rockstar Games. This site is not affiliated with or endorsed by Take-Two Interactive or Rockstar Games.
        </div>
        <div className="credit">
            Made by Rio
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CountdownApp />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Router>
  );
};

export default App;
